/**
 * toastStore.ts
 *
 * Zustand store for toast notification management.
 */

import { create } from 'zustand';

// ============================================================================
// Types
// ============================================================================

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  type: ToastType;
  title?: string;
  message: string;
  duration?: number; // 0 = persistent
}

export interface ToastState {
  toasts: Toast[];

  addToast: (toast: Omit<Toast, 'id'>) => string;
  dismissToast: (id: string) => void;
  clearAllToasts: () => void;

  // Convenience methods
  success: (message: string, title?: string) => string;
  error: (message: string, title?: string) => string;
  warning: (message: string, title?: string) => string;
  info: (message: string, title?: string) => string;
}

// ============================================================================
// Helpers
// ============================================================================

let toastId = 0;
const generateId = () => `toast-${++toastId}-${Date.now()}`;

// ============================================================================
// Store
// ============================================================================

export const useToastStore = create<ToastState>((set, get) => ({
  toasts: [],

  addToast: (toast) => {
    const id = generateId();
    const newToast: Toast = {
      ...toast,
      id,
      duration: toast.duration ?? 5000,
    };

    set((state) => ({
      toasts: [...state.toasts, newToast].slice(-5), // Keep max 5 toasts
    }));

    return id;
  },

  dismissToast: (id) => {
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    }));
  },

  clearAllToasts: () => {
    set({ toasts: [] });
  },

  // Convenience methods
  success: (message, title) => {
    return get().addToast({ type: 'success', message, title });
  },

  error: (message, title) => {
    return get().addToast({ type: 'error', message, title, duration: 8000 });
  },

  warning: (message, title) => {
    return get().addToast({ type: 'warning', message, title });
  },

  info: (message, title) => {
    return get().addToast({ type: 'info', message, title });
  },
}));

export default useToastStore;
